import React, { useState, useEffect } from "react";
import { X, Globe, Shield, Activity, BookmarkPlus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { domainStore } from "../store/domainStore";

export interface AddDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultDomain?: string;
  defaultSource?: "client_profile" | "manual";
  onSuccess?: (domain: string, source: "client_profile" | "manual") => void;
}

export const AddDomainModal: React.FC<AddDomainModalProps> = ({
  isOpen,
  onClose,
  defaultDomain = "",
  defaultSource = "client_profile",
  onSuccess,
}) => {
  const [domain, setDomain] = useState("");
  const [label, setLabel] = useState("");
  const [source, setSource] = useState<"client_profile" | "manual">(defaultSource);
  const [includeSubdomains, setIncludeSubdomains] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const clean = defaultDomain
        .replace(/^(https?:\/\/)?(www\.)?/, "")
        .split("/")[0]
        .split(":")[0]
        .trim();
      setDomain(clean);
      setLabel(clean ? clean.split(".")[0].toUpperCase() : "");
      setSource(defaultSource);
      setIncludeSubdomains(true);
    }
  }, [isOpen, defaultDomain, defaultSource]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanDomain = domain
      .replace(/^(https?:\/\/)?(www\.)?/, "")
      .split("/")[0]
      .split(":")[0]
      .toLowerCase()
      .trim();

    if (!cleanDomain || !cleanDomain.includes(".")) {
      toast.error("Please enter a valid domain (e.g. company.com)");
      return;
    }

    setSubmitting(true);
    try {
      await domainStore.addDomain(cleanDomain, {
        source,
        label: label.trim() || cleanDomain,
        include_subdomains: includeSubdomains,
      });

      const modeName = source === "client_profile" ? "Continuous Monitoring" : "Watchlist";
      toast.success(`Added ${cleanDomain} to ${modeName}`);
      onSuccess?.(cleanDomain, source);
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || "Failed to add domain to monitoring";
      toast.error(typeof msg === "string" ? msg : "Failed to add domain");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
        zIndex: 2100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--card-bg, #111318)",
          borderRadius: "10px",
          width: "100%",
          maxWidth: "500px",
          padding: "24px",
          position: "relative",
          border: "1px solid var(--card-border, #1f2430)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                background: "rgba(63, 182, 139, 0.12)",
                border: "1px solid rgba(63, 182, 139, 0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#3fb68b",
              }}
            >
              <Globe size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "var(--color-text, #f1f5f9)" }}>
                Add Monitored Asset
              </h3>
              <p style={{ fontSize: "12px", color: "var(--color-text-muted, #94a3b8)", margin: "2px 0 0 0" }}>
                Configure continuous threat monitoring and exposure tracking
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--color-text-subtle, #64748b)",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
            }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Domain Input */}
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--color-text, #f1f5f9)", marginBottom: "6px" }}>
              Corporate Domain <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="e.g. acme.com or portal.acme.com"
                required
                autoFocus
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid var(--card-border, #334155)",
                  background: "var(--color-surface-2, #181b22)",
                  color: "var(--color-text, #f1f5f9)",
                  fontSize: "13px",
                  fontFamily: "monospace",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* Label / Organization */}
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--color-text, #f1f5f9)", marginBottom: "6px" }}>
              Asset / Company Label <span style={{ fontSize: "11px", fontWeight: 400, color: "var(--color-text-subtle, #64748b)" }}>(Optional)</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Acme Corp Primary"
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "6px",
                border: "1px solid var(--card-border, #334155)",
                background: "var(--color-surface-2, #181b22)",
                color: "var(--color-text, #f1f5f9)",
                fontSize: "13px",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Monitoring Mode Selection */}
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--color-text, #f1f5f9)", marginBottom: "8px" }}>
              Monitoring Tier &amp; Mode
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {/* Option A: Continuous Monitoring */}
              <div
                onClick={() => setSource("client_profile")}
                style={{
                  border: `1.5px solid ${source === "client_profile" ? "#2dd260" : "var(--card-border, #334155)"}`,
                  background: source === "client_profile" ? "rgba(45, 210, 96, 0.08)" : "var(--color-surface-2, #181b22)",
                  borderRadius: "8px",
                  padding: "12px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 700,
                      color: "#2dd260",
                      background: "rgba(45, 210, 96, 0.15)",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#2dd260" }} />
                    LIVE 24/7
                  </span>
                  {source === "client_profile" && <Check size={14} color="#2dd260" />}
                </div>
                <strong style={{ fontSize: "12px", color: "var(--color-text, #f1f5f9)" }}>Continuous Monitor</strong>
                <p style={{ margin: 0, fontSize: "10.5px", color: "var(--color-text-muted, #94a3b8)", lineHeight: 1.35 }}>
                  Continuous company asset tracking with real-time exposure alerts.
                </p>
              </div>

              {/* Option B: Manual Watchlist */}
              <div
                onClick={() => setSource("manual")}
                style={{
                  border: `1.5px solid ${source === "manual" ? "#3fb68b" : "var(--card-border, #334155)"}`,
                  background: source === "manual" ? "rgba(63, 182, 139, 0.08)" : "var(--color-surface-2, #181b22)",
                  borderRadius: "8px",
                  padding: "12px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 700,
                      color: "#3fb68b",
                      background: "rgba(63, 182, 139, 0.15)",
                      padding: "2px 6px",
                      borderRadius: "4px",
                    }}
                  >
                    WATCHLIST
                  </span>
                  {source === "manual" && <Check size={14} color="#3fb68b" />}
                </div>
                <strong style={{ fontSize: "12px", color: "var(--color-text, #f1f5f9)" }}>Manual Watchlist</strong>
                <p style={{ margin: 0, fontSize: "10.5px", color: "var(--color-text-muted, #94a3b8)", lineHeight: 1.35 }}>
                  Quick ad-hoc threat tracking and lookups in your personal watchlist.
                </p>
              </div>
            </div>
          </div>

          {/* Subdomains checkbox */}
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "var(--color-text-muted, #94a3b8)", cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={includeSubdomains}
              onChange={(e) => setIncludeSubdomains(e.target.checked)}
              style={{ accentColor: "#3fb68b" }}
            />
            <span>Include all subdomains (<code>*.{domain || "domain.com"}</code>)</span>
          </label>

          {/* Footer Actions */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginTop: "8px", paddingTop: "14px", borderTop: "1px solid var(--card-border, #1f2430)" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: "8px 14px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: 600,
                background: "transparent",
                color: "var(--color-text-muted, #94a3b8)",
                border: "1px solid var(--card-border, #334155)",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !domain.trim()}
              style={{
                padding: "8px 18px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: 600,
                background: source === "client_profile" ? "#2dd260" : "#3fb68b",
                color: "#0f172a",
                border: "none",
                cursor: submitting || !domain.trim() ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                opacity: submitting || !domain.trim() ? 0.6 : 1,
                boxShadow: "0 2px 8px rgba(45, 210, 96, 0.25)",
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <span>Add to {source === "client_profile" ? "Continuous Monitor" : "Watchlist"}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
